/**
 * Widget HTML Template Generation
 * Generates the complete widget HTML structure
 */

import { generateWidgetCSS } from '../styles/widget-css.js';
import { LAUNCHER_ICONS } from '../components/launcher.js';

/**
 * Generate the home screen HTML
 * @param {Object} config - Widget configuration
 * @returns {string} HTML string
 */
export function generateHomeScreenHTML(config) {
  return `
        <div id="fyncall-home-screen">
            <div class="fyncall-home-header">
                <div class="fyncall-home-topbar">
                    <div class="fyncall-home-branding">
                        <div class="fyncall-home-logo" id="fyncall-home-logo">
                            ${config.companyLogo ?
      `<img src="${config.companyLogo}" alt="Logo">` :
      `<span class="fyncall-home-logo-text">${(config.companyName || 'FY').substring(0, 2).toUpperCase()}</span>`
    }
                        </div>
                        <span class="fyncall-home-company-name">${config.companyName || 'Company Name'}</span>
                    </div>
                </div>
                <div class="fyncall-home-greeting">
                    <h2 class="fyncall-home-title">${config.welcomeTitle || 'Hi there!'}</h2>
                    <p class="fyncall-home-subtitle">${config.welcomeSubtitle || 'How can we help you today?'}</p>
                </div>
            </div>

            <div class="fyncall-home-content">
                <!-- FAQ Topics (shown first, above conversation starter) -->
                <div class="fyncall-faq-section" id="fyncall-home-faqs"></div>

                <!-- Conversation Starter Card (at bottom) -->
                <div class="fyncall-conversation-starter" id="fyncall-conversation-starter">
                    <div class="fyncall-starter-header">
                        <div class="fyncall-starter-avatars">
                            ${config.agentAvatarUrl ?
      `<div class="fyncall-starter-avatar has-image"><img src="${config.agentAvatarUrl}" alt="Agent"></div>` :
      `<div class="fyncall-starter-avatar"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>`
    }
                        </div>
                        <div class="fyncall-starter-info">
                            <h3>${config.agentName || 'Support Team'}</h3>
                            <p><span class="fyncall-online-indicator"></span> Online now</p>
                        </div>
                    </div>
                    <button class="fyncall-start-chat-btn" id="fyncall-start-chat-btn">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        <span>${config.startChatText || 'Start a conversation'}</span>
                    </button>
                </div>

                <!-- Hidden Chat Entry (for backwards compatibility) -->
                <button class="fyncall-chat-entry" id="fyncall-start-chat" style="display:none;"></button>
            </div>

            <!-- Footer -->
            ${config.showPoweredBy ? `<div class="fyncall-home-footer">
                <a href="https://fyncall.com" target="_blank">${config.poweredByText || 'Powered by Fyncall'}</a>
            </div>` : ''}
        </div>
    `;
}

/**
 * Generate the chat header HTML
 * @param {Object} config - Widget configuration
 * @returns {string} HTML string
 */
export function generateChatHeaderHTML(config) {
  const companyInitials = (config.companyName || 'FY').substring(0, 2).toUpperCase();
  // Determine header title based on settings
  const headerDisplayTitle = config.useCompanyNameAsHeader
    ? (config.companyName || 'Chat with us')
    : (config.headerTitle || 'Support Team');

  return `
        <div id="fyncall-header">
            <div id="fyncall-header-info">
                <div class="fyncall-header-avatar" id="fyncall-header-avatar">
                    ${config.companyLogo ?
      `<img src="${config.companyLogo}" alt="" onerror="this.parentElement.innerHTML='${companyInitials}'">` :
      (config.agentAvatarUrl ?
        `<img src="${config.agentAvatarUrl}" alt="" onerror="this.parentElement.innerHTML='${companyInitials}'">` :
        companyInitials)}
                </div>
                <div id="fyncall-header-text">
                    <h3>${headerDisplayTitle}</h3>
                    <p><span class="fyncall-online-dot"></span>${config.subtitle || 'Usually replies in minutes'}</p>
                </div>
            </div>
            <div class="fyncall-header-actions">
                <button class="fyncall-header-action fyncall-header-minimize" id="fyncall-minimize" title="Minimize">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
                </button>
                ${(config.homeScreenEnabled || config.transcriptEnabled || config.showSoundToggle || config.showDarkModeToggle) ? `
                <div class="fyncall-header-menu">
                    <button class="fyncall-header-action" id="fyncall-menu-btn" title="More options">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
                        </svg>
                    </button>
                    <div class="fyncall-header-menu-dropdown" id="fyncall-menu-dropdown">
                        ${config.showSoundToggle ? `<button class="fyncall-menu-item" id="fyncall-sound-toggle"><svg viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg><span>Sound on</span></button>` : ''}
                        ${config.showDarkModeToggle ? `<button class="fyncall-menu-item" id="fyncall-darkmode-toggle"><svg viewBox="0 0 24 24"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg><span>Dark mode</span></button>` : ''}
                        ${(config.showSoundToggle || config.showDarkModeToggle) && config.homeScreenEnabled ? `<div class="fyncall-menu-divider"></div>` : ''}
                        ${config.homeScreenEnabled ? `<button class="fyncall-menu-item danger" id="fyncall-end-conversation"><svg viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg><span>End conversation</span></button>` : ''}
                    </div>
                </div>
                ` : ''}
            </div>
        </div>
        <div id="fyncall-status"></div>
    `;
}

/**
 * Generate the pre-chat form HTML
 * @returns {string} HTML string
 */
export function generatePrechatFormHTML() {
  return `
        <div class="fyncall-prechat hidden" id="fyncall-prechat">
            <div class="fyncall-prechat-header">
                <div class="fyncall-prechat-avatar">👋</div>
                <h3>Hi there!</h3>
                <p>We'd love to get to know you better</p>
            </div>
            <div class="fyncall-prechat-form">
                <div class="fyncall-prechat-field">
                    <label>Your name <span class="optional">(optional)</span></label>
                    <input type="text" id="fyncall-prechat-name" placeholder="What should we call you?">
                </div>
                <div class="fyncall-prechat-field">
                    <label>Email <span class="optional">(optional)</span></label>
                    <input type="email" id="fyncall-prechat-email" placeholder="your@email.com">
                </div>
                <div class="fyncall-prechat-actions">
                    <button class="fyncall-prechat-continue" id="fyncall-prechat-continue">
                        Start chatting
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    </button>
                    <div class="fyncall-prechat-divider">or</div>
                    <button class="fyncall-prechat-skip" id="fyncall-prechat-skip">
                        Skip, just start chatting →
                    </button>
                </div>
            </div>
        </div>
    `;
}

/**
 * Generate the messages area HTML
 * @returns {string} HTML string
 */
export function generateMessagesAreaHTML() {
  return `
        <div id="fyncall-messages">
            <div class="fyncall-typing">
                <div class="fyncall-typing-dots"><span></span><span></span><span></span></div>
                <span class="fyncall-typing-text">Luna is typing...</span>
            </div>
        </div>
    `;
}

/**
 * Generate the file preview HTML
 * @returns {string} HTML string
 */
export function generateFilePreviewHTML() {
  return `
        <div class="fyncall-file-preview" id="fyncall-file-preview">
            <img class="fyncall-file-preview-thumb" id="fyncall-file-thumb" src="" alt="">
            <div class="fyncall-file-preview-info">
                <div class="fyncall-file-preview-name" id="fyncall-file-name"></div>
                <div class="fyncall-file-preview-size" id="fyncall-file-size"></div>
            </div>
            <button class="fyncall-file-preview-remove" id="fyncall-file-remove">&times;</button>
        </div>
    `;
}

/**
 * Generate the input area HTML
 * @param {Object} config - Widget configuration
 * @returns {string} HTML string
 */
export function generateInputAreaHTML(config) {
  return `
        <div id="fyncall-input-area">
                <div class="fyncall-input-wrapper">
                    <textarea id="fyncall-input" placeholder="${config.inputPlaceholder || 'Type your message...'}" rows="1" disabled></textarea>
                    ${config.enableFileUpload !== false ? `
                    <button class="fyncall-input-action" id="fyncall-attach" title="Attach file">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                        </svg>
                    </button>
                    ` : ''}
                <button id="fyncall-send" disabled>
                  <svg  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path>
                    <path d="m21.854 2.147-10.94 10.939"></path>
                  </svg>
                </button>
            </div>
        </div>
        ${config.showPoweredBy ? `<div class="fyncall-powered">${config.poweredByText || 'Powered by Fyncall'}</div>` : ''}
        <input type="file" id="fyncall-file-input" accept="image/*,.pdf,.doc,.docx">
    `;
}

/**
 * Generate the cart panel HTML
 * @returns {string} HTML string
 */
export function generateCartPanelHTML() {
  return `
        <div class="fyncall-cart-panel" id="fyncall-cart-panel">
            <div class="fyncall-cart-header">
                <button class="fyncall-cart-back" id="fyncall-cart-back">←</button>
                <span class="fyncall-cart-title">Your Cart</span>
                <span class="fyncall-cart-count" id="fyncall-cart-panel-count">0</span>
            </div>
            <!-- Free Shipping Progress -->
            <div class="fyncall-cart-shipping-progress" id="fyncall-cart-shipping-progress" style="display: none;">
                <div class="fyncall-cart-shipping-text" id="fyncall-cart-shipping-text"></div>
                <div class="fyncall-cart-shipping-bar">
                    <div class="fyncall-cart-shipping-fill" id="fyncall-cart-shipping-fill" style="width: 0%"></div>
                </div>
            </div>
            <div class="fyncall-cart-items" id="fyncall-cart-items">
                <div class="fyncall-cart-empty">
                    <div class="fyncall-cart-empty-icon">🛒</div>
                    <div class="fyncall-cart-empty-text">Your cart is empty</div>
                </div>
            </div>
            <!-- Promo Code Section -->
            <div class="fyncall-cart-promo" id="fyncall-cart-promo" style="display: none;">
                <button class="fyncall-cart-promo-toggle" id="fyncall-cart-promo-toggle">
                    🏷️ Have a promo code?
                </button>
                <div class="fyncall-cart-promo-form" id="fyncall-cart-promo-form">
                    <input type="text" class="fyncall-cart-promo-input" id="fyncall-cart-promo-input" placeholder="Enter code">
                    <button class="fyncall-cart-promo-btn" id="fyncall-cart-promo-btn">Apply</button>
                </div>
                <div class="fyncall-cart-promo-error" id="fyncall-cart-promo-error" style="display: none;"></div>
            </div>
            <div class="fyncall-cart-footer" id="fyncall-cart-footer" style="display: none;">
                <!-- Delivery Estimate -->
                <div class="fyncall-cart-delivery" id="fyncall-cart-delivery">
                    <span class="fyncall-cart-delivery-icon">🚚</span>
                    <div class="fyncall-cart-delivery-text">
                        <strong>Estimated Delivery</strong>
                        <span id="fyncall-cart-delivery-date">3-5 business days</span>
                    </div>
                </div>
                <!-- Savings Display -->
                <div class="fyncall-cart-savings" id="fyncall-cart-savings" style="display: none;">
                    <span><span class="fyncall-cart-savings-icon">🎉</span> You're saving</span>
                    <span class="fyncall-cart-savings-amount" id="fyncall-cart-savings-amount">$0.00</span>
                </div>
                <!-- Order Summary -->
                <div class="fyncall-cart-summary" id="fyncall-cart-summary">
                    <div class="fyncall-cart-summary-row">
                        <span>Subtotal</span>
                        <span id="fyncall-cart-subtotal">$0.00</span>
                    </div>
                    <div class="fyncall-cart-summary-row" id="fyncall-cart-discount-row" style="display: none;">
                        <span>Discount</span>
                        <span id="fyncall-cart-discount">-$0.00</span>
                    </div>
                    <div class="fyncall-cart-summary-row">
                        <span>Shipping</span>
                        <span id="fyncall-cart-shipping">Calculated at checkout</span>
                    </div>
                    <div class="fyncall-cart-summary-row total">
                        <span>Estimated Total</span>
                        <span id="fyncall-cart-total">$0.00</span>
                    </div>
                </div>
                <button class="fyncall-checkout-btn" id="fyncall-checkout-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                    Proceed to Checkout
                </button>
            </div>
        </div>
    `;
}

/**
 * Generate the chat view HTML
 * @param {Object} config - Widget configuration
 * @returns {string} HTML string
 */
export function generateChatViewHTML(config) {
  return `
        <div id="fyncall-chat-view">
            ${generateChatHeaderHTML(config)}
            ${generatePrechatFormHTML()}
            ${generateMessagesAreaHTML()}
            ${generateFilePreviewHTML()}
            ${generateInputAreaHTML(config)}
            ${generateCartPanelHTML()}
        </div>
    `;
}

/**
 * Generate the CSAT survey panel HTML
 * @returns {string} HTML string
 */
export function generateCsatPanelHTML() {
  return `
        <div class="fyncall-csat" id="fyncall-csat">
            <div class="fyncall-csat-header">
                <h3>Feedback</h3>
            </div>
            <div class="fyncall-csat-content" id="fyncall-csat-content">
                <p class="fyncall-csat-question" id="fyncall-csat-question">How would you rate your experience?</p>
                <!-- Star Rating (default) -->
                <div class="fyncall-csat-stars" id="fyncall-csat-stars">
                    <span class="fyncall-csat-star" data-rating="1">☆</span>
                    <span class="fyncall-csat-star" data-rating="2">☆</span>
                    <span class="fyncall-csat-star" data-rating="3">☆</span>
                    <span class="fyncall-csat-star" data-rating="4">☆</span>
                    <span class="fyncall-csat-star" data-rating="5">☆</span>
                </div>
                <!-- Thumbs Rating -->
                <div class="fyncall-csat-thumbs" id="fyncall-csat-thumbs" style="display: none;">
                    <button class="fyncall-csat-thumb up" data-rating="positive">👍</button>
                    <button class="fyncall-csat-thumb down" data-rating="negative">👎</button>
                </div>
                <!-- NPS Rating (0-10) -->
                <div class="fyncall-csat-nps" id="fyncall-csat-nps" style="display: none;">
                    ${Array.from({ length: 11 }, (_, i) => `<button class="fyncall-csat-nps-btn" data-rating="${i}">${i}</button>`).join('')}
                </div>
                <div class="fyncall-csat-nps-labels" id="fyncall-csat-nps-labels" style="display: none;">
                    <span>Not likely</span>
                    <span>Very likely</span>
                </div>
                <!-- Follow-up feedback -->
                <div class="fyncall-csat-followup" id="fyncall-csat-followup" style="display: none;">
                    <label id="fyncall-csat-followup-label">Any additional feedback? (optional)</label>
                    <textarea id="fyncall-csat-feedback" placeholder="Tell us more..."></textarea>
                </div>
            </div>
            <!-- Thank you message -->
            <div class="fyncall-csat-thankyou" id="fyncall-csat-thankyou">
                <div class="fyncall-csat-thankyou-icon">🎉</div>
                <h3>Thank you!</h3>
                <p id="fyncall-csat-thankyou-text">Your feedback helps us improve.</p>
            </div>
            <div class="fyncall-csat-actions" id="fyncall-csat-actions">
                <button class="fyncall-csat-submit" id="fyncall-csat-submit" disabled>Submit Feedback</button>
                <button class="fyncall-csat-skip" id="fyncall-csat-skip">Skip</button>
            </div>
        </div>
    `;
}

/**
 * Generate modals HTML (variant selection, end chat)
 * @returns {string} HTML string
 */
export function generateModalsHTML() {
  return `
        <!-- Variant Selection Modal -->
        <div class="fyncall-modal-overlay" id="fyncall-variant-modal">
            <div class="fyncall-modal">
                <div class="fyncall-modal-header">
                    <h3 class="fyncall-modal-title">Select Options</h3>
                    <button class="fyncall-modal-close" id="fyncall-variant-modal-close">&times;</button>
                </div>
                <div class="fyncall-modal-body" id="fyncall-variant-modal-body">
                    <!-- Dynamic content -->
                </div>
                <div class="fyncall-modal-footer">
                    <button class="fyncall-product-btn secondary" id="fyncall-variant-cancel">Cancel</button>
                    <button class="fyncall-product-btn primary" id="fyncall-variant-add">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 20c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM20 20c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                        Add to Cart
                    </button>
                </div>
            </div>
        </div>

        <!-- End Chat Modal -->
        <div class="fyncall-modal-overlay fyncall-end-chat-modal" id="fyncall-end-chat-modal">
            <div class="fyncall-modal">
                <div class="fyncall-modal-body">
                    <div class="fyncall-end-chat-icon">
                        <svg viewBox="0 0 24 24" stroke-width="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                    </div>
                    <div class="fyncall-end-chat-title">Chat Ended</div>
                    <div class="fyncall-end-chat-subtitle">Thank you for chatting with us! Would you like a copy of this conversation?</div>
                    <div class="fyncall-end-chat-actions">
                        <button class="fyncall-end-chat-btn primary" id="fyncall-download-transcript">
                            <svg viewBox="0 0 24 24" stroke-width="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            Download Transcript
                        </button>
                        <button class="fyncall-end-chat-btn secondary" id="fyncall-end-chat-close">
                            No thanks, close chat
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Generate the proactive message HTML
 * @param {Object} config - Widget configuration
 * @returns {string} HTML string
 */
export function generateProactiveHTML(config) {
  return `
        <div class="fyncall-proactive" id="fyncall-proactive">
            <button class="fyncall-proactive-close" id="fyncall-proactive-close">&times;</button>
            <p>${config.proactiveMessage || 'Hi there! Need help finding something?'}</p>
            <button class="fyncall-proactive-btn" id="fyncall-proactive-btn">Chat with us</button>
        </div>
    `;
}

/**
 * Generate the launcher button HTML
 * @param {Object} config - Widget configuration
 * @param {Function} getLauncherIcon - Function to get launcher icon
 * @returns {string} HTML string
 */
export function generateLauncherHTML(config, getLauncherIcon) {
  const iconHTML = getLauncherIcon ? getLauncherIcon() : (LAUNCHER_ICONS[config.launcherIcon] || LAUNCHER_ICONS.chat);

  return `
        <button id="fyncall-launcher" class="launcher-${config.launcherStyle || 'circle'} launcher-${config.launcherSize || 'medium'} ${config.launcherAnimation !== 'none' ? 'animation-' + config.launcherAnimation : ''}">
            ${iconHTML}
            ${config.launcherStyle === 'pill' && config.launcherText ? `<span class="launcher-text">${config.launcherText}</span>` : ''}
            <span class="fyncall-badge" id="fyncall-badge">0</span>
        </button>
    `;
}

/**
 * Generate the complete widget HTML
 * @param {Object} config - Widget configuration
 * @param {Object} darkColors - Dark mode color overrides
 * @param {boolean} isSDKMode - Whether running in SDK mode
 * @param {Function} getLauncherIcon - Function to get launcher icon
 * @returns {string} Complete HTML string
 */
export function generateWidgetHTML(config, darkColors, isSDKMode, getLauncherIcon) {
  return `
        <style>${generateWidgetCSS(config, darkColors, isSDKMode)}</style>

        <div id="fyncall-chat-window">
            ${generateHomeScreenHTML(config)}
            ${generateChatViewHTML(config)}
            ${generateCsatPanelHTML()}
        </div>

        ${generateModalsHTML()}
        ${generateProactiveHTML(config)}
        ${isSDKMode ? '' : generateLauncherHTML(config, getLauncherIcon)}
    `;
}

/**
 * Query and return all widget elements from container
 * @param {HTMLElement} container - The widget container element
 * @returns {Object} Object containing all element references
 */
export function queryWidgetElements(container) {
  return {
    container: container,
    launcher: container.querySelector('#fyncall-launcher'),
    chatWindow: container.querySelector('#fyncall-chat-window'),
    messages: container.querySelector('#fyncall-messages'),
    input: container.querySelector('#fyncall-input'),
    sendBtn: container.querySelector('#fyncall-send'),
    typing: container.querySelector('.fyncall-typing'),
    status: container.querySelector('#fyncall-status'),
    badge: container.querySelector('#fyncall-badge'),
    proactive: container.querySelector('#fyncall-proactive'),
    proactiveClose: container.querySelector('#fyncall-proactive-close'),
    proactiveBtn: container.querySelector('#fyncall-proactive-btn'),
    attachBtn: container.querySelector('#fyncall-attach'),
    fileInput: container.querySelector('#fyncall-file-input'),
    filePreview: container.querySelector('#fyncall-file-preview'),
    fileThumb: container.querySelector('#fyncall-file-thumb'),
    fileName: container.querySelector('#fyncall-file-name'),
    fileSize: container.querySelector('#fyncall-file-size'),
    fileRemove: container.querySelector('#fyncall-file-remove'),
    // Cart elements
    headerCart: container.querySelector('#fyncall-header-cart'),
    headerCartBadge: container.querySelector('#fyncall-header-cart-badge'),
    cartPanel: container.querySelector('#fyncall-cart-panel'),
    cartBack: container.querySelector('#fyncall-cart-back'),
    cartPanelCount: container.querySelector('#fyncall-cart-panel-count'),
    cartItems: container.querySelector('#fyncall-cart-items'),
    cartFooter: container.querySelector('#fyncall-cart-footer'),
    cartSubtotal: container.querySelector('#fyncall-cart-subtotal'),
    checkoutBtn: container.querySelector('#fyncall-checkout-btn'),
    // Enhanced Cart elements
    cartShippingProgress: container.querySelector('#fyncall-cart-shipping-progress'),
    cartShippingText: container.querySelector('#fyncall-cart-shipping-text'),
    cartShippingFill: container.querySelector('#fyncall-cart-shipping-fill'),
    cartPromo: container.querySelector('#fyncall-cart-promo'),
    cartPromoToggle: container.querySelector('#fyncall-cart-promo-toggle'),
    cartPromoForm: container.querySelector('#fyncall-cart-promo-form'),
    cartPromoInput: container.querySelector('#fyncall-cart-promo-input'),
    cartPromoBtn: container.querySelector('#fyncall-cart-promo-btn'),
    cartPromoError: container.querySelector('#fyncall-cart-promo-error'),
    cartDelivery: container.querySelector('#fyncall-cart-delivery'),
    cartDeliveryDate: container.querySelector('#fyncall-cart-delivery-date'),
    cartSavings: container.querySelector('#fyncall-cart-savings'),
    cartSavingsAmount: container.querySelector('#fyncall-cart-savings-amount'),
    cartSummary: container.querySelector('#fyncall-cart-summary'),
    cartDiscountRow: container.querySelector('#fyncall-cart-discount-row'),
    cartDiscount: container.querySelector('#fyncall-cart-discount'),
    cartShipping: container.querySelector('#fyncall-cart-shipping'),
    cartTotal: container.querySelector('#fyncall-cart-total'),
    // Variant modal elements
    variantModal: container.querySelector('#fyncall-variant-modal'),
    variantModalBody: container.querySelector('#fyncall-variant-modal-body'),
    variantModalClose: container.querySelector('#fyncall-variant-modal-close'),
    variantCancel: container.querySelector('#fyncall-variant-cancel'),
    variantAdd: container.querySelector('#fyncall-variant-add'),
    // End chat modal elements
    endChatModal: container.querySelector('#fyncall-end-chat-modal'),
    endChatDownload: container.querySelector('#fyncall-download-transcript'),
    endChatClose: container.querySelector('#fyncall-end-chat-close'),
    // Pre-chat form elements
    prechatForm: container.querySelector('#fyncall-prechat'),
    prechatName: container.querySelector('#fyncall-prechat-name'),
    prechatEmail: container.querySelector('#fyncall-prechat-email'),
    prechatContinue: container.querySelector('#fyncall-prechat-continue'),
    prechatSkip: container.querySelector('#fyncall-prechat-skip'),
    // Minimize button
    minimizeBtn: container.querySelector('#fyncall-minimize'),
    // End conversation button
    endConversationBtn: container.querySelector('#fyncall-end-conversation'),
    // Header menu dropdown
    menuBtn: container.querySelector('#fyncall-menu-btn'),
    menuDropdown: container.querySelector('#fyncall-menu-dropdown'),
    // Home screen elements
    homeScreen: container.querySelector('#fyncall-home-screen'),
    homeClose: container.querySelector('#fyncall-home-close'),
    homeSearchInput: container.querySelector('#fyncall-home-search-input'),
    starterSend: container.querySelector('#fyncall-starter-send'),
    conversationStarter: container.querySelector('#fyncall-conversation-starter'),
    chatView: container.querySelector('#fyncall-chat-view'),
    chatBack: container.querySelector('#fyncall-chat-back'),
    startChat: container.querySelector('#fyncall-start-chat'),
    startChatBtn: container.querySelector('#fyncall-start-chat-btn'),
    // Phase 2: Dynamic home screen containers
    homeFaqs: container.querySelector('#fyncall-home-faqs'),
    homeFaqsLabel: container.querySelector('#fyncall-faqs-label'),
    homeActions: container.querySelector('#fyncall-home-actions'),
    homeActionsLabel: container.querySelector('#fyncall-actions-label'),
    // CSAT Survey elements
    csatPanel: container.querySelector('#fyncall-csat'),
    csatContent: container.querySelector('#fyncall-csat-content'),
    csatQuestion: container.querySelector('#fyncall-csat-question'),
    csatStars: container.querySelector('#fyncall-csat-stars'),
    csatThumbs: container.querySelector('#fyncall-csat-thumbs'),
    csatNps: container.querySelector('#fyncall-csat-nps'),
    csatNpsLabels: container.querySelector('#fyncall-csat-nps-labels'),
    csatFollowup: container.querySelector('#fyncall-csat-followup'),
    csatFollowupLabel: container.querySelector('#fyncall-csat-followup-label'),
    csatFeedback: container.querySelector('#fyncall-csat-feedback'),
    csatThankyou: container.querySelector('#fyncall-csat-thankyou'),
    csatThankyouText: container.querySelector('#fyncall-csat-thankyou-text'),
    csatActions: container.querySelector('#fyncall-csat-actions'),
    csatSubmit: container.querySelector('#fyncall-csat-submit'),
    csatSkip: container.querySelector('#fyncall-csat-skip')
  };
}
